import { useZero } from "@rocicorp/zero/react";
import { type ReactNode, useEffect, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { useHasAccess } from "../hooks/useHasAccess";
import { type MoveTarget, useMoveTargets } from "../hooks/useMoveTargets";
import { useMoveWorkflow } from "../hooks/useMoveWorkflow";
import { useRecordLinkCopied } from "../hooks/useRecordLinkCopied";
import { useRecordNavigationClick } from "../hooks/useRecordNavigationClick";
import { useSessionTenantOrigin } from "../hooks/useSessionTenantOrigin";
import { spaceIcon } from "../lib/spaceIcon";
import { useMakeUrl, routes } from "../lib/urls";
import { writeToClipboard } from "../lib/writeToClipboard";
import { Button } from "./Button";
import { ConfirmModal } from "./ConfirmModal";
import { Dropdown } from "./Dropdown";
import { HelpTooltip } from "./HelpTooltip";
import { Icon } from "./Icon";
import { LabelWithInfo } from "./LabelWithInfo";
import { MoveWorkflowModal } from "./MoveWorkflowModal";
import { Tooltip } from "./Tooltip";
import { WorkflowSettings } from "./WorkflowSettings";

type Props = {
  workflowId: string;
  spaceId: string;
  hasLiveVersion: boolean;
  isFavorite: boolean;
  autofixEnabled: boolean;
  autoImproveEnabled: boolean;
  opener?: ReactNode;
  afterDelete?: () => void;
};

function MoveTargetsLoader({
  onTargets,
}: {
  onTargets: (targets: MoveTarget[]) => void;
}) {
  const targets = useMoveTargets();
  useEffect(() => {
    onTargets(targets);
  }, [targets, onTargets]);
  return null;
}

export function WorkflowActionsMenu({
  workflowId,
  spaceId,
  hasLiveVersion,
  isFavorite,
  autofixEnabled,
  autoImproveEnabled,
  opener,
  afterDelete,
}: Props) {
  const makeUrl = useMakeUrl();
  const recordClick = useRecordNavigationClick();
  const recordLinkCopied = useRecordLinkCopied();
  const z = useZero();
  const canWrite = useHasAccess("space:write", spaceId);
  const canPushLive = useHasAccess("space:push_live", spaceId);
  const canCreateSpace = useHasAccess("tenant:space_create");
  const canTakeOffline = canWrite && (!hasLiveVersion || canPushLive);
  const {
    requestMove,
    pending: movePending,
    confirmMove,
    cancelMove,
  } = useMoveWorkflow();
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveTargets, setMoveTargets] = useState<MoveTarget[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [retentionOpen, setRetentionOpen] = useState(false);
  const tenantOrigin = useSessionTenantOrigin();

  const workflowUrl = makeUrl(routes.space.workflow, { spaceId, workflowId });

  const availableTargets = moveTargets.filter((s) => s.id !== spaceId);
  const createSpaceItem = canCreateSpace
    ? [
        {
          type: "link" as const,
          left: <Icon name="plus" size={16} />,
          label: "Create space",
          to: makeUrl(routes.modal.createSpace, { workflowId }),
          onClick: () => recordClick("workflow_menu", "create_space"),
        },
      ]
    : [];
  const moveSubmenuItem = ({ disabled }: { disabled?: boolean }) => ({
    type: "submenu" as const,
    left: <Icon name="input" size={16} />,
    label: "Move",
    disabled,
    searchable: availableTargets.length >= 10,
    searchEmptyLabel: "No matching spaces",
    submenu: [
      {
        items:
          availableTargets.length > 0
            ? availableTargets.map((s) => ({
                type: "button" as const,
                key: s.id,
                disabled: s.disabled,
                searchText: s.name,
                left: <Icon name={spaceIcon(s.mode)} size={16} />,
                label: s.disabledReason ? (
                  <Tooltip content={s.disabledReason}>
                    <span>{s.name}</span>
                  </Tooltip>
                ) : (
                  s.name
                ),
                onClick: () =>
                  requestMove({
                    workflowId,
                    targetSpaceId: s.id,
                    targetSpaceName: s.name,
                  }),
              }))
            : createSpaceItem,
      },
    ],
  });

  const autofixLabelNode = (
    <LabelWithInfo>
      <span>Autofix</span>
      <HelpTooltip content="When enabled, 3B monitors for errors and automatically creates a fix branch." />
    </LabelWithInfo>
  );

  const autoImproveLabelNode = (
    <LabelWithInfo>
      <span>Autotune</span>
      <HelpTooltip content="When enabled, 3B continuously looks for ways to make this workflow more reliable, faster, or more capable, and proposes them on a branch." />
    </LabelWithInfo>
  );

  const primaryItems = [
    {
      type: "button" as const,
      left: <Icon name="link-2" size={16} />,
      label: "Copy link",
      onClick: () => {
        void writeToClipboard(tenantOrigin + workflowUrl).then(
          (ok) => ok && recordLinkCopied("workflow")
        );
      },
    },
    {
      type: "button" as const,
      left: <Icon name={isFavorite ? "heart-crack" : "heart"} size={16} />,
      label: isFavorite ? "Remove favorite" : "Favorite",
      onClick: () => {
        z.mutate(sharedMutators.toggleWorkflowFavorite({ workflowId }));
      },
    },
    {
      type: "button" as const,
      keepOpen: true,
      left: <Icon name="radar" size={16} />,
      label: autofixLabelNode,
      disabled: !canWrite,
      toggled: autofixEnabled,
      onClick: () => {
        z.mutate(sharedMutators.toggleAutofix({ workflowId }));
      },
    },
    {
      type: "button" as const,
      keepOpen: true,
      left: <Icon name="audio-waveform" size={16} />,
      label: autoImproveLabelNode,
      disabled: !canWrite,
      toggled: autoImproveEnabled,
      onClick: () => {
        z.mutate(sharedMutators.toggleAutoImprove({ workflowId }));
      },
    },
    {
      type: "button" as const,
      left: <Icon name="calendar" size={16} />,
      label: "Execution retention",
      disabled: !canWrite,
      onClick: () => {
        recordClick("workflow_menu", "execution_retention");
        setRetentionOpen(true);
      },
    },
    moveSubmenuItem({ disabled: !canTakeOffline }),
  ];

  return (
    <>
      {menuOpen && <MoveTargetsLoader onTargets={setMoveTargets} />}
      <Dropdown
        opener={opener ?? <Button variant="secondary" icon="more" />}
        minWidth={180}
        onOpenChange={setMenuOpen}
        sections={[
          { items: primaryItems },
          {
            items: [
              {
                type: "button" as const,
                left: <Icon name="trash" size={16} />,
                label: "Delete",
                disabled: !canTakeOffline,
                onClick: () => setConfirmDeleteOpen(true),
              },
            ],
          },
        ]}
      />
      {movePending && (
        <MoveWorkflowModal
          workflowId={movePending.workflowId}
          targetSpaceId={movePending.targetSpaceId}
          targetSpaceName={movePending.targetSpaceName}
          onClose={cancelMove}
          onConfirm={confirmMove}
        />
      )}
      {retentionOpen && (
        <WorkflowSettings
          workflowId={workflowId}
          onClose={() => setRetentionOpen(false)}
        />
      )}
      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          z.mutate(sharedMutators.deleteWorkflow({ workflowId }));
          setConfirmDeleteOpen(false);
          afterDelete?.();
        }}
        title="Delete workflow"
        description="This workflow and all its steps will be deleted. You can recover it from the space menu for 30 days."
        confirmLabel="Delete workflow"
        destructive
      />
    </>
  );
}
